'use client';

import { Globe, ExternalLink } from 'lucide-react';
import type { BaseTileProps, SearchResult } from '../types';
import { cn } from '@/lib/utils';
import { ScoreBadge, SourceBadge } from './BaseTile';
import { activateOnKey } from '@/lib/a11y/activateOnKey';

export function WebTile({
  result,
  mode,
  showScore,
  showSource,
  highlighted,
  listboxOption,
  gridCell,
  onClick,
  className,
}: BaseTileProps<SearchResult>) {
  const domain = (result.metadata?.domain as string) || (() => {
    try { return new URL(result.url || '').hostname.replace('www.', ''); } catch { return ''; }
  })();

  return (
    <div
      className={cn(
        'px-3 py-2.5 rounded-lg cursor-pointer transition-colors',
        highlighted ? 'bg-blue-500/15' : 'hover:bg-slate-800/50',
        className
      )}
      id={gridCell?.id ?? listboxOption?.id}
      role={gridCell ? 'gridcell' : listboxOption ? 'option' : 'button'}
      aria-selected={gridCell?.selected ?? listboxOption?.selected}
      tabIndex={listboxOption || gridCell ? -1 : 0}
      onClick={onClick}
      onKeyDown={activateOnKey(onClick)}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-7 h-7 rounded-md bg-blue-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Globe className="w-3.5 h-3.5 text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm text-blue-300 truncate">{result.title}</h4>
            <ExternalLink className="w-3 h-3 text-slate-600 flex-shrink-0" />
            {showScore && result.score > 0 && <ScoreBadge score={result.score} />}
            {showSource && <SourceBadge source={result.source} />}
          </div>
          {domain && (
            <span className="text-[10px] text-slate-500 block mt-0.5">{domain}</span>
          )}
          {result.preview && mode === 'expanded' && (
            <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{result.preview}</p>
          )}
        </div>
      </div>
    </div>
  );
}
